import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  PersonAddAlt1Outlined as JoinIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import EventCard from '../../components/community/EventCard';
import { EventCardSkeleton, Loading, StackSkeleton } from '../../components/layout/Skeletons';
import { useAgenda } from '../../hooks/community/useAgenda';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage } from '../../utils/apiMessages';
import { usePhotoUploads } from '../../hooks/community/usePhotoUploads';
import PhotoPicker from '../../components/community/PhotoPicker';

const EMPTY = {
  title: '',
  startsAt: '',
  endsAt: '',
  description: '',
  location: '',
  neighborhoodUuid: '',
};

/**
 * Turn what a datetime-local input gives us into what the API expects.
 *
 * The input has no timezone, so it means "local time"; the API wants an
 * offset, and the backend normalises to UTC from there.
 */
function toIsoWithOffset(localValue) {
  if (!localValue) return null;

  const date = new Date(localValue);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function EventComposer({ neighborhoods, onCreated }) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const photos = usePhotoUploads();

  /* The same way in as the feed's, where the button will be once they have
     an account: the lede says one is needed to organise anything. */
  if (!isAuthenticated) {
    return (
      <Button
        component={RouterLink}
        to="/register"
        variant="contained"
        size="large"
        startIcon={<JoinIcon />}
      >
        Créer un compte
      </Button>
    );
  }

  const update = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const submit = async (event) => {
    event.preventDefault();
    setSending(true);
    setError('');

    try {
      const response = await apiCall('/events', {
        method: 'POST',
        body: JSON.stringify({
          title: form.title.trim(),
          startsAt: toIsoWithOffset(form.startsAt),
          endsAt: toIsoWithOffset(form.endsAt),
          description: form.description.trim() || null,
          location: form.location.trim() || null,
          neighborhoodUuid: form.neighborhoodUuid || null,
          mediaUuids: photos.uuids,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(translateApiMessage(data.error) || 'Votre événement n’a pas pu être créé.');
        return;
      }

      toast.success('Votre événement est à l’agenda.');
      setForm(EMPTY);
      photos.reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
        Organiser un événement
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <form onSubmit={submit}>
          <DialogTitle>Organiser un événement</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Titre"
                value={form.title}
                onChange={update('title')}
                required
                disabled={sending}
                inputProps={{ maxLength: 180 }}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Début"
                  type="datetime-local"
                  value={form.startsAt}
                  onChange={update('startsAt')}
                  required
                  disabled={sending}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Fin (facultatif)"
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={update('endsAt')}
                  disabled={sending}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Stack>

              <TextField
                label="Lieu"
                value={form.location}
                onChange={update('location')}
                disabled={sending}
                inputProps={{ maxLength: 255 }}
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={update('description')}
                disabled={sending}
                multiline
                minRows={4}
                inputProps={{ maxLength: 5000 }}
              />

              {neighborhoods.length > 0 && (
                <TextField
                  select
                  label="Quartier (facultatif)"
                  value={form.neighborhoodUuid}
                  onChange={update('neighborhoodUuid')}
                  disabled={sending}
                >
                  <MenuItem value="">Toute la ville</MenuItem>
                  {neighborhoods.map((neighborhood) => (
                    <MenuItem key={neighborhood.uuid} value={neighborhood.uuid}>
                      {neighborhood.name}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <PhotoPicker uploads={photos} disabled={sending} />

              {error && <Alert severity="error">{error}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} disabled={sending} variant="outlined">
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={sending || photos.uploading}>
              {photos.uploading ? 'Envoi des photos…' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
}

/**
 * Free text over the agenda, applied on submit rather than on every keystroke.
 *
 * A draft of its own, following the URL: arriving with `?q=` from a search
 * elsewhere has to fill the field with what is actually being searched.
 */
function AgendaSearchField({ value, onSearch }) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <Box
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch(draft.trim());
      }}
      sx={{ flexGrow: 1 }}
    >
      <TextField
        label="Rechercher"
        id="agenda-search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Un titre, un lieu"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

/**
 * What is happening in the neighbourhood.
 */
function Agenda() {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const {
    filters,
    events,
    meta,
    city,
    neighborhoods,
    loading,
    error,
    setFilter,
    reload,
  } = useAgenda();

  const cancel = async (event) => {
    const response = await apiCall(`/events/${event.uuid}`, { method: 'DELETE' });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      toast.error(translateApiMessage(data.error) || 'L’événement n’a pas pu être annulé.');
      return;
    }

    toast.success('Événement annulé.');
    reload();
  };

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          reserveLede
          title="L’agenda du quartier"
          lede={
            city
              ? `Vide-greniers, réunions, concerts : ce qui se passe à ${city.name} dans les prochains jours. Tout le monde peut lire ; il faut un compte pour créer un événement.`
              : 'Ce qui se passe près de chez vous dans les prochains jours.'
          }
          action={<EventComposer neighborhoods={neighborhoods} onCreated={reload} />}
        />

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2.5, md: 3 },
            mb: 3.5,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2.5,
            alignItems: { md: 'center' },
          }}
        >
          {/* The same free text the feed and the directory take, so a search
              from the home page can be carried here and still mean
              something. */}
          <AgendaSearchField value={filters.q} onSearch={(value) => setFilter('q', value)} />

          <TextField
            select
            label="Quartier"
            id="agenda-neighborhood"
            // Shown only once it is among the options: the list arrives after the
            // filter, which the URL already carries, and MUI warns on a value it
            // cannot show. The filter itself stays applied meanwhile.
            value={neighborhoods.some((n) => n.slug === filters.neighborhood) ? filters.neighborhood : ''}
            onChange={(e) => setFilter('neighborhood', e.target.value)}
            sx={{ minWidth: { md: 280 } }}
            disabled={0 === neighborhoods.length}
          >
            <MenuItem value="">Tout le quartier</MenuItem>
            {neighborhoods.map((neighborhood) => (
              <MenuItem key={neighborhood.slug} value={neighborhood.slug}>
                {neighborhood.name}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            sx={{ ml: { md: 1 } }}
            control={
              <Switch
                checked={filters.past}
                onChange={(e) => setFilter('past', e.target.checked ? '1' : '')}
              />
            }
            label={<Typography variant="body1">Voir aussi ce qui est passé</Typography>}
          />
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Loading label="Chargement de l’agenda">
            <StackSkeleton count={3}>{EventCardSkeleton}</StackSkeleton>
          </Loading>
        ) : (
          <Stack spacing={2.25}>
            {0 === events.length && (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
                <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                  Rien de prévu pour l’instant.
                </Typography>
                <Typography variant="body1">
                  {isAuthenticated
                    ? 'Organisez quelque chose : une réunion, un vide-grenier, un nettoyage.'
                    : 'Créez un compte pour annoncer le premier événement.'}
                </Typography>
              </Paper>
            )}

            {events.map((event) => (
              <EventCard key={event.uuid} event={event} onDelete={cancel} onModerated={reload} />
            ))}
          </Stack>
        )}

        {meta && meta.total_pages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
            <Pagination
              count={meta.total_pages}
              page={meta.current_page}
              onChange={(event, page) => setFilter('page', String(page))}
            />
          </Box>
        )}
      </Box>
    </PageLayout>
  );
}

export default Agenda;
